import en from '../locales/en.json';
import fr from '../locales/fr.json';

const DECISIONS = ['approved', 'denied', 'delegated'];

const personName = (person) => {
  if (!person) return '';
  const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim();
  return fullName || person.username || '';
};

const pendingApprovers = (item) => {
  const group = Array.isArray(item.approver_group) ? item.approver_group.filter(Boolean) : [];
  return group.length ? group : [item.approver].filter(Boolean);
};

// Line events come from /approvals/line-item; older rows used `status` and `approver`.
const toDecision = (event) => {
  const action = String(event?.action || event?.status || '').toLowerCase();
  if (!DECISIONS.includes(action)) return null;
  const assigned = event.assigned_approver || event.approver;
  return {
    entry: {
      kind: action,
      person: personName(event.actor || assigned),
      onBehalfOf: event.acted_on_behalf ? personName(assigned) : '',
      at: event.acted_at || event.updated_date || null,
      reason: (event.reason || '').trim(),
      // XPEN-3838: a delegation names who the line went to.
      ...(action === 'delegated' ? { delegatedTo: personName(event.delegated_to) } : {}),
    },
    sentTo: personName(assigned),
    requestedAt: event.requested_at || null,
  };
};

const requestStep = (person, at) => ({ kind: 'requested', person, onBehalfOf: '', at: at || null, reason: '' });

const byDate = (a, b) => new Date(a.at || 0) - new Date(b.at || 0);

const lineDecisions = (item) => (Array.isArray(item?.approval_history) ? item.approval_history : [])
  .map(toDecision)
  .filter(Boolean)
  .sort((a, b) => byDate(a.entry, b.entry));

/**
 * The line's approval steps, oldest first: each request (who it was sent to, and when)
 * before its decision, ending with who the line waits for. Dates are aware ISO strings
 * written by the API, shown in the viewer's time zone.
 */
export const lineApprovalEntries = (item) => {
  if (!item) return [];
  const entries = [];
  lineDecisions(item).forEach((decision) => {
    if (decision.requestedAt) entries.push(requestStep(decision.sentTo, decision.requestedAt));
    entries.push(decision.entry);
  });
  if (item.Status === 'Need_Approval') {
    const waitingFor = pendingApprovers(item).map(personName).filter(Boolean).join(', ');
    if (waitingFor) {
      entries.push(requestStep(waitingFor, item.approval_requested_at || item.approver?.requested_date));
      entries.push({ kind: 'waiting', person: waitingFor, onBehalfOf: '', at: null, reason: '' });
    }
  } else if (entries.length && !entries.some((entry) => entry.kind === 'requested') && item.approval_requested_at) {
    // Decided before events kept their request date: the line still carries it.
    const first = lineDecisions(item)[0];
    entries.unshift(requestStep(first.sentTo || first.entry.person, item.approval_requested_at));
  }
  return entries;
};

// The API writes "Requested via workflow: …" and "Requested via GL responsible: …" when it routes a line.
const isRoutingNote = (line) => line.trim().startsWith('Requested via');

// The invoice screen logs each request as "date : name : SECTION : Request approval sent To …", in the sender's language.
const REQUEST_LOG_MARKERS = [en, fr].map((locale) => ` : ${locale.request_approval} ${locale.sent} ${locale.to} `.toLowerCase());
const isRequestLog = (line) => REQUEST_LOG_MARKERS.some((marker) => line.toLowerCase().includes(marker));

/**
 * Denials made from the invoice screen are only kept in the line's status_details text,
 * so that text is shown without the routing notes and request logs, which record no decision.
 */
export const lineApprovalDetails = (item) => (item?.status_details || '')
  .split('\n')
  .filter((line) => line.trim() && !isRoutingNote(line) && !isRequestLog(line))
  .join('\n')
  .trim();

export const hasLineApprovalHistory = (item) =>
  lineApprovalEntries(item).length > 0 || lineApprovalDetails(item) !== '';
